import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fmt } from "../lib/calc";
import { groupByJob } from "../lib/bank";
import { Archive, Loader2, Star, X, Check } from "lucide-react";

// Pick panels from the ACTIVE drawing bank to add to the current day's queue.
// Selected panels are moved to the "completed" bank server-side, then returned
// so the parent can drop them into the queue.
export function BankPicker({ onClose, onAdd }) {
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bank");
        setPanels((data.panels || []).filter((p) => (p.status || "active") === "active"));
      } catch {
        toast.error("Could not load the bank");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleJob = (items) => {
    const ids = items.map((p) => p.id);
    const allSel = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const n = new Set(prev);
      ids.forEach((id) => (allSel ? n.delete(id) : n.add(id)));
      return n;
    });
  };

  const groups = groupByJob(panels);

  const add = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setAdding(true);
    try {
      const { data } = await api.post("/bank/move", { ids, status: "completed" });
      onAdd(data.panels || []);
      toast.success(`Added ${ids.length} panel${ids.length !== 1 ? "s" : ""} to the queue`);
    } catch {
      toast.error("Could not add panels");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div data-testid="bank-picker-modal" onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[92vh] flex flex-col bg-[#16181A] border border-[#2E3033]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2E3033] shrink-0">
          <div className="flex items-center gap-2">
            <Archive className="w-4 h-4 text-[#2560C9]" />
            <h3 className="font-heading font-bold text-white text-sm tracking-tight">ADD FROM DRAWING BANK</h3>
          </div>
          <button data-testid="bank-picker-close" onClick={onClose} className="text-[#6E7787] hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="text-center text-[#6E7787] font-mono text-sm py-12"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…</div>
          ) : groups.length === 0 ? (
            <div className="text-center text-[#6E7787] text-sm font-mono py-12 border border-dashed border-[#2E3033]">
              Drawing Bank is empty. Upload drawings from the <span className="text-[#3B75DF]">Bank</span> tab first.
            </div>
          ) : (
            groups.map((g) => {
              const allSel = g.items.every((p) => selected.has(p.id));
              return (
                <div key={g.job} data-testid={`bank-picker-group-${g.job}`} className="border border-[#2E3033]">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-[#2E3033] bg-[#0A0A0A]">
                    {g.priority && <Star className="w-3.5 h-3.5 text-[#F59E0B]" fill="#F59E0B" />}
                    <span className="font-heading font-bold text-white text-sm tracking-[0.12em]">{g.job}</span>
                    {g.priority && <span className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-[#F59E0B]/15 border border-[#F59E0B]/50 text-[#F59E0B]">Priority</span>}
                    <button
                      data-testid={`bank-picker-selectjob-${g.job}`}
                      onClick={() => toggleJob(g.items)}
                      className="ml-auto text-[10px] font-mono uppercase tracking-wider text-[#3B75DF] hover:text-white"
                    >
                      {allSel ? "Deselect all" : "Select all"}
                    </button>
                  </div>
                  <div className="divide-y divide-[#2E3033]">
                    {g.items.map((p) => (
                      <label
                        key={p.id}
                        data-testid={`bank-picker-panel-${p.id}`}
                        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-[#22252A]"
                      >
                        <input
                          type="checkbox"
                          data-testid={`bank-picker-check-${p.id}`}
                          checked={selected.has(p.id)}
                          onChange={() => toggle(p.id)}
                          className="accent-[#2560C9] w-4 h-4"
                        />
                        {p.priority && <Star className="w-3 h-3 text-[#F59E0B] shrink-0" fill="#F59E0B" />}
                        <span className="w-2 h-2 shrink-0" style={{ backgroundColor: p.color }} />
                        <span className="font-heading font-bold text-white text-sm">{p.name}</span>
                        <span className="text-[10px] font-mono text-[#6E7787] ml-auto">{fmt(p.width)} × {fmt(p.length)} m · {fmt(p.volume, 2)} m³</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-[#2E3033] shrink-0">
          <span className="text-[11px] font-mono text-[#6E7787]">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button data-testid="bank-picker-cancel" onClick={onClose} className="border border-[#2E3033] hover:bg-[#22252A] text-[#A0AABF] text-xs px-3 py-1.5 transition-colors">Cancel</button>
            <button
              data-testid="bank-picker-add"
              onClick={add}
              disabled={adding || selected.size === 0}
              className="flex items-center gap-1.5 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-xs px-4 py-1.5 transition-colors disabled:opacity-40"
            >
              {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Add to Queue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
